import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Envelope, Question, Headset } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SupportPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/support`, formData);
      toast.success('Support message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-16 md:py-24 px-4 sm:px-6" data-testid="support-page">
      <div className="w-full max-w-5xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground mb-4">WE'RE HERE TO HELP</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-none font-light mb-4 md:mb-6" data-testid="support-heading">
            Support Center
          </h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            Have questions or need assistance? We're here to help you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-12">
          <Card className="p-6 text-center" data-testid="support-feature-email">
            <Envelope size={48} weight="duotone" className="text-primary mx-auto mb-4" />
            <h3 className="text-xl tracking-tight font-light mb-2">Email Support</h3>
            <p className="text-sm text-muted-foreground">support@truthconfession.com</p>
          </Card>

          <Card className="p-6 text-center" data-testid="support-feature-faq">
            <Question size={48} weight="duotone" className="text-primary mx-auto mb-4" />
            <h3 className="text-xl tracking-tight font-light mb-2">FAQ</h3>
            <p className="text-sm text-muted-foreground">Find answers to common questions</p>
          </Card>

          <Card className="p-6 text-center" data-testid="support-feature-live">
            <Headset size={48} weight="duotone" className="text-primary mx-auto mb-4" />
            <h3 className="text-xl tracking-tight font-light mb-2">Live Chat</h3>
            <p className="text-sm text-muted-foreground">Available 9 AM - 6 PM EST</p>
          </Card>
        </div>

        <Card className="p-8" data-testid="support-form-container">
          <h2 className="text-3xl tracking-tight font-light mb-6">Send Us a Message</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="support-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="support-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                type="text"
                placeholder="How can we help?"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                data-testid="support-subject-input"
              />
            </div>

            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Please describe your issue or question in detail..."
                rows={6}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                data-testid="support-message-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full md:w-auto rounded-full bg-primary hover:bg-primary/90 px-8"
              disabled={loading}
              data-testid="support-submit-button"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </Card>

        <div className="mt-12 text-center">
          <h3 className="text-2xl tracking-tight font-light mb-6">Frequently Asked Questions</h3>
          <div className="space-y-4 text-left max-w-3xl mx-auto">
            <Card className="p-6" data-testid="faq-item-1">
              <h4 className="text-lg font-medium mb-2">How do I upgrade my membership?</h4>
              <p className="text-sm text-muted-foreground">
                Visit the Membership page and choose the plan that suits you best. Follow the payment process to upgrade.
              </p>
            </Card>

            <Card className="p-6" data-testid="faq-item-2">
              <h4 className="text-lg font-medium mb-2">Is my identity truly anonymous?</h4>
              <p className="text-sm text-muted-foreground">
                Yes, when you choose to post anonymously, your identity is completely hidden from other users.
              </p>
            </Card>

            <Card className="p-6" data-testid="faq-item-3">
              <h4 className="text-lg font-medium mb-2">What are chat credits?</h4>
              <p className="text-sm text-muted-foreground">
                Free users get 50 chat credits to try out the platform. Elite members get unlimited credits.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}